import { createStore, createEvent } from "effector"
import { useMemo } from "react"
import { useUnit } from "effector-react"

import StorageService from "services/StorageService"
import { StorageKey, ViewSettingsStorage, ViewSettingsVersion } from "config/constants"
import User from "models/User"
import ViewSettingsInventory, { IViewSettingsInventory } from "models/ViewSettingsInventory"
import ViewSettingsRequests, { IViewSettingsRequests } from "models/ViewSettingsRequests"
import ViewSettingsShippingOrders, { IViewSettingsShippingOrders } from "models/ViewSettingsShippingOrders"
import ViewSettingsNotifications, { IViewSettingsNotifications } from "models/ViewSettingsNotifications"
import ViewSettingsOffers, { IViewSettingsOffers } from "models/ViewSettingsOffers"
import ViewSettingsOutgoingInvoices, { IViewSettingsOutgoingInvoices } from "models/ViewSettingsOutgoingInvoices"
import ViewSettingsIncomingInvoices, { IViewSettingsIncomingInvoices } from 'models/ViewSettingsIncomingInvoices';
import ViewSettingsLinkLabels, { IViewSettingsLinkLabels } from "models/ViewSettingsLinkLabels"
import ViewSettingsGlobalAssetOverview, { IViewSettingsGlobalAssetOverview } from "models/ViewSettingsGlobalAssetOverview"
import ViewSettingsMaintenance, { IViewSettingsMaintenance } from "models/ViewSettingsMaintenance"
import ViewSettingsFairValueEvaluation from "models/ViewSettingsFairValueEvaluation"


interface IViewSettingsStore {
    stores: {
        [ViewSettingsStorage.inventory]: ViewSettingsInventory
        [ViewSettingsStorage.requests]: ViewSettingsRequests
        [ViewSettingsStorage.offers]: ViewSettingsOffers
        [ViewSettingsStorage.shippingOrders]: ViewSettingsShippingOrders
        [ViewSettingsStorage.outgoingInvoices]: ViewSettingsOutgoingInvoices
        [ViewSettingsStorage.notifications]: ViewSettingsNotifications
        [ViewSettingsStorage.linkLabels]: ViewSettingsLinkLabels
        [ViewSettingsStorage.globalAssetOverview]: ViewSettingsGlobalAssetOverview
        [ViewSettingsStorage.maintenance]: ViewSettingsMaintenance
        [ViewSettingsStorage.fairValueEvaluation]: ViewSettingsRequests
    }
    version: number,
    user?: string,
    tenant?: string,
}

type ViewSettingsAny =
    ViewSettingsInventory
    | ViewSettingsRequests
    | ViewSettingsShippingOrders
    | ViewSettingsOutgoingInvoices
    | ViewSettingsNotifications
    | ViewSettingsOffers
    | ViewSettingsIncomingInvoices
    | ViewSettingsLinkLabels
    | ViewSettingsGlobalAssetOverview
    | ViewSettingsMaintenance
    | ViewSettingsFairValueEvaluation

const setViewSettings = createEvent<{ store: ViewSettingsStorage, viewSettings: ViewSettingsAny }>()
const setUser = createEvent<{ user?: string, tenant?: string }>()
const reset = createEvent()

const viewSettingsDefault = {
    stores: {
        [ViewSettingsStorage.inventory]: new ViewSettingsInventory({}),
        [ViewSettingsStorage.requests]: new ViewSettingsRequests({}),
        [ViewSettingsStorage.offers]: new ViewSettingsOffers({}),
        [ViewSettingsStorage.shippingOrders]: new ViewSettingsShippingOrders({}),
        [ViewSettingsStorage.outgoingInvoices]: new ViewSettingsOutgoingInvoices({}),
        [ViewSettingsStorage.notifications]: new ViewSettingsNotifications({}),
        [ViewSettingsStorage.incomingInvoices]: new ViewSettingsIncomingInvoices({}),
        [ViewSettingsStorage.linkLabels]: new ViewSettingsLinkLabels({}),
        [ViewSettingsStorage.globalAssetOverview]: new ViewSettingsGlobalAssetOverview({}),
        [ViewSettingsStorage.maintenance]: new ViewSettingsMaintenance({}),
        [ViewSettingsStorage.fairValueEvaluation]: new ViewSettingsRequests({}),
    },
    version: ViewSettingsVersion
}

const viewSettings = createStore<IViewSettingsStore>(getInitialViewSettingsStore())

viewSettings.on(setViewSettings, (state: IViewSettingsStore, { store, viewSettings: newViewSettings }) => {
    return {
        ...state,
        stores: {
            ...state.stores,
            [store]: newViewSettings
        }
    }
})

viewSettings.on(setUser, (state: IViewSettingsStore, user) => {
    return {
        ...state,
        ...user
    }
})

viewSettings.on(reset, () => viewSettingsDefault)

viewSettings.watch(state => {
    StorageService.setItem(StorageKey.viewSettings, state)
})

function getInitialViewSettingsStore() {
    try {
        const viewSettingsJson = StorageService.getItem(StorageKey.viewSettings)
        if (!viewSettingsJson) {
            return viewSettingsDefault
        }
        const store: IViewSettingsStore = JSON.parse(viewSettingsJson)
        const existingStores = Object.entries(store.stores).reduce((acc, [key, value]) => {
            if (ViewSettingsStorage[key]) {
                switch (ViewSettingsStorage[key]) {
                    case ViewSettingsStorage.inventory:
                        acc[key] = new ViewSettingsInventory(value as IViewSettingsInventory)
                        break
                    case ViewSettingsStorage.requests:
                        acc[key] = new ViewSettingsRequests(value as IViewSettingsRequests)
                        break
                    case ViewSettingsStorage.offers:
                        acc[key] = new ViewSettingsOffers(value as IViewSettingsOffers)
                        break
                    case ViewSettingsStorage.shippingOrders:
                        acc[key] = new ViewSettingsShippingOrders(value as IViewSettingsShippingOrders)
                        break
                    case ViewSettingsStorage.outgoingInvoices:
                        acc[key] = new ViewSettingsOutgoingInvoices(value as IViewSettingsOutgoingInvoices)
                        break
                    case ViewSettingsStorage.incomingInvoices:
                        acc[key] = new ViewSettingsIncomingInvoices(value as IViewSettingsIncomingInvoices)
                        break
                    case ViewSettingsStorage.notifications:
                        acc[key] = new ViewSettingsNotifications(value as IViewSettingsNotifications)
                        break
                    case ViewSettingsStorage.linkLabels:
                        acc[key] = new ViewSettingsLinkLabels(value as IViewSettingsLinkLabels)
                        break
                    case ViewSettingsStorage.globalAssetOverview:
                        acc[key] = new ViewSettingsGlobalAssetOverview(value as IViewSettingsGlobalAssetOverview)
                        break
                    case ViewSettingsStorage.maintenance:
                        acc[key] = new ViewSettingsMaintenance(value as IViewSettingsMaintenance)
                        break
                    case ViewSettingsStorage.fairValueEvaluation:
                        acc[key] = new ViewSettingsRequests(value as IViewSettingsRequests)
                        break
                }
            }
            return acc
        }, {})
        store.stores = {
            ...viewSettingsDefault.stores,
            ...existingStores,
        }
        return store
    } catch (e) {
        console.log(e)
    }
    return viewSettingsDefault

}

async function initViewSettingsStoreUser(user: User | null) {
    const state = viewSettings.getState()
    if (!user?.email
        || user?.email !== state.user
        || !user?.companyId
        || user?.companyId !== state.tenant) {
        reset()
    }
    setUser({
        user: user?.email,
        tenant: user?.companyId,
    })
}

function useViewSettings<ViewSettingsType extends ViewSettingsAny>(storage: ViewSettingsStorage) {
    const vs = useUnit(viewSettings)
    const viewSettingsStore = useMemo(() => {

        return vs.stores[storage]
    }, [storage, vs.stores])

    return {
        viewSettings: viewSettingsStore as ViewSettingsType,
        setViewSettings: (newViewSettings: ViewSettingsType) => setViewSettings({ store: storage, viewSettings: newViewSettings })
    }
}

export default {
    viewSettings,
    setViewSettings,
    initViewSettingsStoreUser,
    useViewSettings,
}
